import React, { useState, useEffect } from 'react';
import { faSquare } from '@fortawesome/pro-light-svg-icons/faSquare';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons/faSquareCheck';
import { useQuizContext } from '../../../../context/QuizContext';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Box } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import {
  EliminationToolIconButtons,
  ExplanationDropDown,
  ExplanationMessage,
} from '../utils';
import { getOptionColor, optionStateEnum } from '../../../../constants/quiz';
import { P } from '@osmosis/react-ui/components/Text';
import { getLetterFromNumber } from '../../../../lib/shared/quiz/answers';
import { QuizExplanationObject } from '../../../../types/quiz';
import { useIntl } from 'react-intl';

type Props = {
  text: string;
  isCorrect: boolean;
  explanation: string | QuizExplanationObject;
  index: number;
  checked: boolean;
  setCheckboxAnswers: React.Dispatch<React.SetStateAction<number[]>>;
};

/**
 *Checkbox option for AnswerCheckboxOption component.
 */
const AnswerCheckboxOption = ({
  text,
  isCorrect,
  explanation,
  setCheckboxAnswers,
  checked,
  index,
}: Props) => {
  const { isAnswered, showElimination, setEliminationItems } = useQuizContext();
  const [optionState, setOptionState] = useState(optionStateEnum.default);
  const [eliminatedAnswer, setEliminatedAnswer] = useState(false);
  const [isExplanation, setExplanation] = useState(false);
  const colors = useColors();
  const intl = useIntl();

  useEffect(() => {
    if (checked) {
      setOptionState(optionStateEnum.chosen);
    }
    if (!checked) {
      setOptionState(optionStateEnum.default);
    }
    if (eliminatedAnswer) {
      setOptionState(optionStateEnum.eliminated);
    }
    if (isCorrect && isAnswered) {
      setOptionState(optionStateEnum.correct);
    }
    if (!isCorrect && isAnswered && checked) {
      setOptionState(optionStateEnum.incorrect);
    }
  }, [isAnswered, isCorrect, checked, eliminatedAnswer]);

  useEffect(() => {
    setEliminatedAnswer(false);
    setEliminationItems([]);
  }, [showElimination, setEliminationItems]);

  const isCorrectAnswer = optionState === optionStateEnum.correct;

  const handleClick = () => {
    if (!isAnswered) {
      setEliminatedAnswer(false);
      setCheckboxAnswers(((prev: number[]) => {
        if (checked) {
          return prev.filter((item) => item !== index);
        }
        return [index, ...prev];
      }) as any);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      handleClick();
      event.preventDefault();
    }
  };

  const letter = getLetterFromNumber(index) + '.';

  return (
    <Flex
      tabIndex={0}
      role="checkbox"
      aria-checked={checked}
      data-testid="answer-checkbox-option"
      alignItems="center"
      mb="10px"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{ cursor: 'pointer' }}
    >
      <Box mr="10px" ml="20px" sx={{ position: 'relative' }}>
        {isCorrectAnswer && (
          <Box
            sx={{ position: 'absolute', left: -4 }}
            aria-label={intl.formatMessage({
              id: 'correct_answer',
              defaultMessage: 'Correct answer:',
            })}
          >
            <Icon icon={faCheck} fontSize="20px" color="green" />
          </Box>
        )}

        {checked ? (
          <Icon icon={faSquareCheck} fontSize="20px" color={colors.primary} />
        ) : (
          <Icon
            icon={faSquare}
            fontSize="20px"
            fontWeight="400"
            color={colors.gray50}
          />
        )}
      </Box>
      <Flex alignItems="flex-start" flexDirection="column" width="100%">
        <Flex
          flexDirection="column"
          py="18px"
          px="14px"
          bg={getOptionColor(optionState).bg}
          width="100%"
          sx={{
            border: '1px solid',
            borderColor: getOptionColor(optionState).borderColor,
            borderRadius: 5,
            ':hover': {
              bg: 'gray5',
            },
          }}
        >
          <Flex sx={{ gap: 2 }} justifyContent="space-between">
            <Flex sx={{ gap: 2 }}>
              <P>{letter}</P>
              <P
                textAlign="left"
                pl="10px"
                sx={{
                  color: getOptionColor(optionState).color,
                  textDecoration: getOptionColor(optionState).textDecoration,
                }}
                display="inline-block"
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
              />
            </Flex>
            <EliminationToolIconButtons
              index={index}
              setEliminatedAnswer={setEliminatedAnswer}
              eliminatedAnswer={eliminatedAnswer}
            />
          </Flex>
          {isAnswered && !isExplanation && (
            <ExplanationDropDown
              setExplanationVisible={setExplanation}
              isOpen={isExplanation}
            />
          )}
        </Flex>
        {isExplanation && <ExplanationMessage explanation={explanation} />}
      </Flex>
    </Flex>
  );
};

export default AnswerCheckboxOption;
